import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  withDeviceType,
  DEVICE_TYPES,
  type AddressForm,
  type DeviceType,
  type EquipmentForm,
  type InstallationForm,
  type LoginRole,
} from "../../api/installations";

/** Address, city, state, PIN: the same four fields for billing and each installation. */
export function AddressFields({
  value,
  onChange,
  disabled,
  required = true,
  errors = {},
}: {
  value: AddressForm;
  onChange: (a: AddressForm) => void;
  disabled?: boolean | undefined;
  required?: boolean;
  /** By field (address, city, state, pin): shown in red under it. */
  errors?: Record<string, string>;
}) {
  const set = (k: keyof AddressForm, v: string) => onChange({ ...value, [k]: v });
  const err = (k: keyof AddressForm) => (errors[k] ? { error: true, helperText: errors[k] } : {});
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth required={required} size="small" label="Address" value={value.address} disabled={disabled} onChange={(e) => set("address", e.target.value)} {...err("address")} />
      </Grid>
      <Grid size={{ xs: 12, sm: 5 }}>
        <TextField fullWidth required={required} size="small" label="City" value={value.city} disabled={disabled} onChange={(e) => set("city", e.target.value)} {...err("city")} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField fullWidth required={required} size="small" label="State" value={value.state} disabled={disabled} onChange={(e) => set("state", e.target.value)} {...err("state")} />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          fullWidth
          required={required}
          size="small"
          label="PIN"
          value={value.pin}
          disabled={disabled}
          onChange={(e) => set("pin", e.target.value)}
          slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 6 } }}
          {...err("pin")}
        />
      </Grid>
    </Grid>
  );
}

/** One equipment row: type, make, model, serial, MAC. */
export function EquipmentRow({
  value,
  onChange,
  onRemove,
  role,
  deviceid,
  disabled,
  macError,
}: {
  value: EquipmentForm;
  onChange: (e: EquipmentForm) => void;
  onRemove?: () => void;
  role: LoginRole;
  deviceid: string;
  disabled?: boolean | undefined;
  /** In red under the MAC: badly formed, or already in use on the site. */
  macError?: string | undefined;
}) {
  const set = (k: Exclude<keyof EquipmentForm, "device_type">, v: string) => onChange({ ...value, [k]: v });
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        select
        size="small"
        label="Device type"
        value={value.device_type}
        disabled={disabled}
        onChange={(e) => onChange(withDeviceType(value, e.target.value as DeviceType, role, deviceid))}
        sx={{ minWidth: 120 }}
      >
        {DEVICE_TYPES.map((d) => (
          <MenuItem key={d.value} value={d.value}>
            {d.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField size="small" label="Make" value={value.make} disabled={disabled} onChange={(e) => set("make", e.target.value)} sx={{ width: 130 }} />
      <TextField size="small" label="Model" value={value.model} disabled={disabled} onChange={(e) => set("model", e.target.value)} sx={{ width: 130 }} />
      <TextField size="small" label="Serial" value={value.serial} disabled={disabled} onChange={(e) => set("serial", e.target.value)} sx={{ width: 150 }} />
      <TextField
        size="small"
        label="MAC"
        value={value.mac_address}
        disabled={disabled}
        onChange={(e) => set("mac_address", e.target.value)}
        error={Boolean(macError)}
        helperText={macError}
        sx={{ width: macError ? 320 : 180 }}
      />
      {onRemove && (
        <IconButton size="small" aria-label="Remove equipment row" onClick={onRemove} disabled={disabled}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

/** One login's installation: "Installed at the billing address" (default on) or its own address; optional equipment rows. */
export function LoginInstallationFields({
  username,
  role,
  deviceid,
  value,
  onChange,
  disabled,
  errors = {},
}: {
  username: string;
  role: LoginRole;
  deviceid: string;
  value: InstallationForm;
  onChange: (f: InstallationForm) => void;
  disabled?: boolean | undefined;
  /** By field: address, city, state, pin, eq.<i>.mac_address. */
  errors?: Record<string, string>;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 1, borderLeft: 2, borderColor: "divider" }}>
      <Typography variant="subtitle2">
        {role === "device" ? "Device login" : "Mobile login"} <code>{username || "—"}</code>
      </Typography>
      <FormControlLabel
        control={<Checkbox size="small" checked={value.same_as_billing} disabled={disabled} onChange={(e) => onChange({ ...value, same_as_billing: e.target.checked })} />}
        label="Installed at the billing address"
      />
      {!value.same_as_billing && <AddressFields value={value.address} onChange={(address) => onChange({ ...value, address })} disabled={disabled} errors={errors} />}
      {/* Owner 2026-10-19: only the ONU's MAC is asked, once -- it is the
          device login. A phone changes its MAC, so the mobile login has none. */}
      <Typography variant="caption" color="text.secondary">
        {role === "device" ? `Its device: the ONU ${deviceid.trim() || "—"}, recorded from the ONU id.` : "No device or MAC here: a phone changes its MAC."}
      </Typography>
    </Box>
  );
}

/**
 * Extra logins (owner 2026-10-19): each is one more device signed in at once.
 * No MAC is asked -- a phone changes its MAC -- so there is nothing to fill in.
 */
export function ExtraLoginsNote({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      Extra logins: {count} more device{count === 1 ? "" : "s"} may sign in at once. No MAC is asked: a phone changes its MAC.
    </Typography>
  );
}
